import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Select } from '@components/core-ui/form-elements/Select';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { UnlockModalErrors } from '@modals/UnlockJobModal/components/errors';
import RefreshBalance from '@components/icons/RefreshBalance.svg';
import { Content } from '@modals/UnlockJobModal/styles/Content';
import { TProps } from '@modals/UnlockJobModal/types/TProps';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { convertDuration } from '@helpers/convertDuration';
import { resolveOrganizationBillingAccountId } from '@helpers/resolveOrganizationBillingAccountId';
import { formatCentsPrice } from '@helpers/formatCentsPrice';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useWebsocketContext } from '@providers/WebsocketProvider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BillingService, JobsService } from 'app/API';
import { useInvalidateQueriesOnChange } from 'app/hooks/useInvalidateQueriesOnChange';
import { useNotifyQueryError } from 'app/hooks/useNotifyQueryError';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { billingKeys } from 'app/queries/billing/billingKeys';
import { createFetchJobSubmissionPaymentMethodsQueryFn } from 'app/queries/billing/createFetchJobSubmissionPaymentMethodsQueryFn';
import { createFetchJobQueryFn } from 'app/queries/jobs/createFetchJobQueryFn';
import { createFetchJobUnlockPricingQueryFn } from 'app/queries/jobs/createFetchJobUnlockPricingQueryFn';
import { jobsKeys } from 'app/queries/jobs/jobsKeys';
import { createFetchAvailableLayoutsQueryFn } from 'app/queries/layouts/createFetchAvailableLayoutsQueryFn';
import { layoutsKeys } from 'app/queries/layouts/layoutsKeys';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@components/core-ui/form-elements/buttons/Button';

export function UnlockJobModalFull({ onCancel, onSuccess, id, name }: TProps) {
  const { updateMe, organization, workspace } = useAuthContext();
  const { openModal, closeModal } = useModalContext();
  const { call } = useAPI();

  const queryClient = useQueryClient();
  const [balanceIsRefreshing, setBalanceIsRefreshing] = useState(false);
  const [chosenCloudLayout, setChosenCloudLayout] = useState<string>();
  const { onWSUpdate } = useWebsocketContext();

  const organizationId = organization?.id ?? '';
  const workspaceId = workspace?.id ?? '';
  const baId = resolveOrganizationBillingAccountId(organization) ?? '';
  const hasContext = !!workspace && !!organization;

  const updateMeRef = useRef(updateMe);
  updateMeRef.current = updateMe;

  useEffect(() => {
    if (hasContext) {
      void updateMeRef.current();
    }
  }, [onWSUpdate, hasContext]);

  const paymentMethodsKey = billingKeys.jobSubmissionPaymentMethodsByAccount(baId);
  const priceKey = jobsKeys.unlockPricing(id);
  useInvalidateQueriesOnChange(onWSUpdate, paymentMethodsKey);
  useInvalidateQueriesOnChange(onWSUpdate, priceKey);

  const paymentMethodsQuery = useQuery({
    queryKey: paymentMethodsKey,
    queryFn: createFetchJobSubmissionPaymentMethodsQueryFn(call),
    enabled: hasContext && baId !== '',
  });
  const priceQuery = useQuery({
    queryKey: priceKey,
    queryFn: createFetchJobUnlockPricingQueryFn(call),
    enabled: hasContext && baId !== '',
  });
  const jobQuery = useQuery({
    queryKey: jobsKeys.workspaceDetail(id, workspaceId),
    queryFn: createFetchJobQueryFn(call),
    enabled: hasContext && baId !== '',
  });
  const layoutsQuery = useQuery({
    queryKey: layoutsKeys.availableByOrganization(organizationId),
    queryFn: createFetchAvailableLayoutsQueryFn(call),
    enabled: hasContext && baId !== '',
  });
  useNotifyQueryError(paymentMethodsQuery.error);
  useNotifyQueryError(priceQuery.error);
  useNotifyQueryError(jobQuery.error);
  useNotifyQueryError(layoutsQuery.error);

  const price = priceQuery.data?.result ?? { duration: 0, alreadyPaid: 0, totalJobCost: 0, amount: 0 };
  const paymentMethodId = paymentMethodsQuery.data?.find((item) => item.default)?.id;
  const selectedCloudLayout = chosenCloudLayout ?? (jobQuery.data?.layoutId || 'null');

  const layoutsList = [
    {
      label: 'None',
      value: 'null',
    },
    ...(layoutsQuery.data || []).map((l) => ({
      value: l.id,
      label: l.name,
    })),
  ];

  const balance = organization?.billingAccount?.currentBalance ?? 0;
  const notEnoughFunds = (price?.amount ?? 0) > balance;
  const noBillingAccount = !resolveOrganizationBillingAccountId(organization);

  const unlockMutation = useMutation({
    mutationFn: async () => {
      await call(() =>
        JobsService.updateLayout({
          jobId: id,
          wsid: workspaceId,
          requestBody: { layoutId: selectedCloudLayout === 'null' ? null : selectedCloudLayout },
        }),
      );
      await call(() => BillingService.unlockJob({ wsid: workspaceId, jobId: id }));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobsKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: jobsKeys.unlockPricing(id) });
      openModal(ModalNamesEnum.ChooseCloudLayout, {
        id,
        name,
        onSuccess,
        onCancel: closeModal,
      });
      onSuccess();
    },
    onError: notifyApiCallError,
  });

  const isLoading =
    paymentMethodsQuery.isLoading ||
    priceQuery.isLoading ||
    jobQuery.isLoading ||
    layoutsQuery.isLoading ||
    unlockMutation.isPending;

  const error = noBillingAccount || (!paymentMethodId && notEnoughFunds) || notEnoughFunds;
  const hasUnlockData = priceQuery.isSuccess && Boolean(priceQuery.data?.result) && jobQuery.isSuccess;

  return (
    <ModalWrapper width="460px">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Unlock Job</ModalTitle>

      <Content>
        <div className="title">Job title</div>
        <div className="title-value">{name || 'Unnamed'}</div>

        <div className="title">Layout</div>
        <Select
          name="layoutId"
          className="layout-select"
          label="Layout"
          value={selectedCloudLayout}
          disabled={layoutsList.length < 2 || isLoading}
          onChange={(_, value) => setChosenCloudLayout(value)}
          options={layoutsList}
          required={false}
        />

        <div className="title">Current balance</div>
        <div className={`title-value ${balanceIsRefreshing ? 'balance-refresh' : ''}`}>
          {formatCentsPrice(balance)}
          <RefreshBalance
            onClick={() => {
              if (balanceIsRefreshing) {
                return;
              }
              setBalanceIsRefreshing(true);

              updateMe()
                .then(() => setBalanceIsRefreshing(false))
                .catch(() => setBalanceIsRefreshing(false));
            }}
          />
        </div>

        <div className="price-description">
          <div>
            <div className="dflex noBorder">
              <span>Duration:</span>
              {convertDuration(price?.duration ?? 0)}
            </div>

            <div className="dflex">
              <span>Total cost:</span>
              {formatCentsPrice(price?.totalJobCost ?? 0)}
            </div>

            <div className="dflex">
              <span>Already paid:</span>
              {formatCentsPrice(price?.alreadyPaid ?? 0)}
            </div>

            <div className="dflex bold">
              <span>In order to unlock this job you will be charged:</span> {formatCentsPrice(price?.amount ?? 0)}
            </div>
          </div>
        </div>

        <div className={`errorsWrapper ${error ? 'active' : ''}`}>
          <UnlockModalErrors
            setBalanceIsRefreshing={setBalanceIsRefreshing}
            price={price}
            onCancel={onCancel}
            dataLoaded={!isLoading}
            onSuccess={onSuccess}
            jobId={id}
            jobName={name}
            isSubmitting={isLoading}
            paymentMethodId={paymentMethodId}
          />
        </div>

        <div className="buttonWrapper">
          <OutlinedButton className="w-full" onClick={onCancel}>
            Cancel
          </OutlinedButton>

          <Button
            className="relative w-full"
            disabled={isLoading || error || !hasUnlockData}
            onClick={() => unlockMutation.mutate()}
          >
            Unlock
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
}
